import authService from "@/services/auth.service";
import axios from "axios";

export const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL as string;

const api = axios.create({
  baseURL: PUBLIC_API_URL + "/api",
});

api.interceptors.request.use((config) => {
  const token = authService.getToken();
  const current_org = JSON.parse(localStorage.getItem("active_org"));
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
    config.headers["x-organization-id"] = current_org;
  }
  return config;
});

/** Route the whole app to the lock-out screen once, not once per request. */
const INACTIVE_ROUTE = "/organization-inactive";

function redirectToInactiveScreen(meta: Record<string, unknown> = {}) {
  if (window.location.pathname === INACTIVE_ROUTE) return;

  const params = new URLSearchParams();
  if (meta.status) params.set("status", String(meta.status));
  if (meta.reason) params.set("reason", String(meta.reason));
  if (meta.lastActivityAt)
    params.set("lastActivityAt", String(meta.lastActivityAt));

  window.location.replace(`${INACTIVE_ROUTE}?${params.toString()}`);
}

/** Where a locked-out subscription sends the user. */
const BILLING_ROUTE = "/billing-required";

function redirectToBillingScreen() {
  if (window.location.pathname === BILLING_ROUTE) return;

  // Only people who can actually pay ever land here, so the screen needs no
  // hint about the audience — the amount and plan are re-fetched by the screen
  // itself, so a stale URL can never show a wrong figure.
  window.location.replace(`${BILLING_ROUTE}?canPay=1`);
}

/**
 * Auth calls are answered on the page the user is already looking at.
 *
 * A passcode sign-in is the one flow that knows the organization up front, so
 * it is where a locked subscription is first noticed — but bouncing the login
 * screen to a payment wall would strand someone who has not signed in yet.
 * Those calls keep their error and the login form prints it.
 */
function isAuthRequest(url?: string) {
  return Boolean(url && url.includes("/auth/"));
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The organization is closed for business: every org-scoped call will fail
    // the same way, so stop the app here instead of raining error toasts.
    if (error?.response?.data?.code === "ORGANIZATION_INACTIVE") {
      redirectToInactiveScreen(error.response.data.meta ?? {});
      return Promise.reject(error);
    }

    // Subscription unpaid and locked by the sweep. Same reasoning as above, and
    // it must come before the 401 retry: refreshing the token would not help.
    //
    // The payment wall is only ever shown to someone who can settle the bill.
    // Everyone else is blocked by the API just the same, but is left where
    // they are with the server's vague message: a cashier cannot pay, so
    // sending them to a screen that asks them to would be a dead end.
    if (error?.response?.data?.code === "SUBSCRIPTION_LOCKED") {
      const meta = error.response.data.meta ?? {};
      if (meta.canManageBilling === true && !isAuthRequest(originalRequest?.url)) {
        redirectToBillingScreen();
      }
      return Promise.reject(error);
    }

    if (error?.response?.status === 401 && !originalRequest._retry) {
      console.log("---- refreshing the token -----");
      originalRequest._retry = true;
      const data = await authService.refresh_token().catch(() => null);

      if (!data?.access_token) {
        return Promise.reject(error);
      }

      const access_token = data?.access_token;
      api.defaults.headers["Authorization"] = "Bearer " + access_token;
      originalRequest.headers["Authorization"] = "Bearer " + access_token;
      return api(originalRequest);
    }
    return Promise.reject(error);
  },
);

export { api };
